import axios from 'axios';

import apiConfig from './apiConfig';

// VidSrc stream API - https://vidsrc.mov
// Every endpoint accepts IMDB (tt-prefixed) or TMDB numeric ids and needs no api key.
//
// Embed urls are handed straight to an iframe, they are never fetched by the app.
// The /vapi feeds list the latest / recently added titles, each entry carrying the
// quality of the source it was added in.

const { baseUrl, subtitlesUrl } = apiConfig.vidsrc;

const vidsrcClient = axios.create({
    baseURL: baseUrl,
    headers: {
        'Content-Type': 'application/json'
    }
});

vidsrcClient.interceptors.response.use((response) => {
    if (response && response.data) {
        return response.data;
    }

    return response;
}, (error) => {
    throw error;
});

// Types accepted by the /vapi/{category}/{type}[/{page}] feeds.
export const listType = {
    latest: 'new',
    added: 'add'
};

// Resolutions the player is known to serve, used to rank the feed entries.
const QUALITY_ALIASES = {
    '8k': 4320,
    '4k': 2160,
    'uhd': 2160,
    'fullhd': 1080,
    'hd': 720,
    'sd': 480
};

const qualityRank = (quality) => {
    const value = String(quality || '').trim().toLowerCase();

    if (QUALITY_ALIASES[value]) return QUALITY_ALIASES[value];

    const pixels = parseInt(value, 10);

    return isNaN(pixels) ? 0 : pixels;
};

// Keeps the best quality entry per title: the feed lists the same title once
// per source it was grabbed in, the highest resolution wins.
export const bestQuality = (results = []) => {
    const best = new Map();

    results.forEach((entry) => {
        const key = entry.imdb_id || entry.tmdb_id;
        const rank = qualityRank(entry.quality);
        const current = best.get(key);

        if (!current || rank > current.rank) {
            best.set(key, { rank, entry });
        }
    });

    return [...best.values()].map((item) => item.entry);
};

const withSubtitles = (url) => {
    if (!subtitlesUrl) return url;

    return `${url}?sub.info=${encodeURIComponent(subtitlesUrl)}`;
};

const feed = (path) => vidsrcClient.get(path).then((res) => ({
    ...res,
    result: bestQuality(res.result)
}));

const vidsrcApi = {
    // GET https://vidsrc.mov/embed/movie/{id}
    embedMovie: (id) => withSubtitles(`${baseUrl}embed/movie/${id}`),
    // GET https://vidsrc.mov/embed/tv/{id}/{season}/{episode}
    embedTv: (id, season, episode) => withSubtitles(`${baseUrl}embed/tv/${id}/${season}/${episode}`),
    // GET https://vidsrc.mov/vapi/movie/{type}[/{page}]
    getMovies: (type, page) => feed(`vapi/movie/${type}${page ? `/${page}` : ''}`),
    // GET https://vidsrc.mov/vapi/tv/{type}[/{page}]
    getTv: (type, page) => feed(`vapi/tv/${type}${page ? `/${page}` : ''}`),
    // GET https://vidsrc.mov/vapi/episode/latest
    getLatestEpisodes: () => feed('vapi/episode/latest')
};

export default vidsrcApi;
